import React from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import Challenges from "../components/Challenges";
import ClientLogosStrip from "../components/ClientLogosStrip";
import { services, caseStudies } from "../mock";
import { ArrowUpRight, ArrowRight, Database, Shield, Sparkles, GitBranch, Boxes, Cloud } from "lucide-react";

const icons = [Database, Shield, Sparkles, GitBranch, Boxes, Cloud];

const Home = () => {
  const featuredServices = services.slice(0, 3);
  const featuredCases = caseStudies.slice(0, 3);

  return (
    <>
      <Hero />
      <Challenges />

      {/* Services teaser */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
            <div className="max-w-2xl">
              <span className="tag-chip">Services</span>
              <h2 className="section-title mt-4">What V4 Data And AI Solutions delivers</h2>
              <p className="section-sub mt-5">
                End-to-end data engineering execution across Microsoft Fabric, Databricks, Snowflake, Synapse and dbt — architected, built and optimized for measurable outcomes.
              </p>
            </div>
            <Link to="/services" className="btn-dark self-start">Explore all services <ArrowUpRight size={16} /></Link>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {featuredServices.map((s, i) => {
              const Icon = icons[i % icons.length];
              return (
                <Link to="/services" key={s.slug} className="reveal group rounded-2xl border border-slate-200 bg-white p-7 hover-lift block">
                  <div className="h-12 w-12 rounded-xl bg-[#0B1B3B] text-white flex items-center justify-center"><Icon size={22} /></div>
                  <h3 className="mt-5 text-xl font-bold text-[#0B1B3B]">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#1E56E3] group-hover:gap-2 transition-all">
                    Learn more <ArrowUpRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case studies teaser */}
      <section className="py-24 bg-[#F5F7FB]">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
            <div className="max-w-2xl">
              <span className="tag-chip">Featured Work</span>
              <h2 className="section-title mt-4">Enterprise outcomes, measured</h2>
              <p className="section-sub mt-5">
                A snapshot of what V4 Data And AI Solutions has shipped — spanning Microsoft Fabric, Snowflake, Databricks and governance programs.
              </p>
            </div>
            <Link to="/insights" className="btn-dark self-start">View all case studies <ArrowUpRight size={16} /></Link>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {featuredCases.map((c) => (
              <Link to={`/case-studies/${c.slug}`} key={c.slug} className="reveal group rounded-2xl overflow-hidden bg-white border border-slate-200 hover-lift block">
                <div className="relative h-48 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/85 to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest text-white bg-[#1E56E3] px-3 py-1 rounded">{c.tag}</span>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="text-3xl font-extrabold leading-none">{c.metric}</div>
                    <div className="text-[10px] uppercase tracking-wider mt-1 text-slate-200">{c.metricLabel}</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0B1B3B] leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{c.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <ClientLogosStrip />
      <section className="py-20 bg-white">
        <div className="container-x">
          <div className="reveal rounded-3xl bg-gradient-to-br from-[#0B1B3B] to-[#132a5c] p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 grid-lines-bg opacity-30" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#7CA6FF] bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">Start with V4 Data And AI Solutions</span>
                <h2 className="mt-4 text-3xl md:text-[42px] font-bold leading-tight text-white">Turn your data platform into a measurable business advantage</h2>
                <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl">
                  Book a discovery conversation and get a target-state architecture, cost baseline and prioritized roadmap for your Databricks, Snowflake or Microsoft Fabric platform.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link to="/contact" className="btn-primary">Talk to us <ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
